import { ChangeEvent } from "react";
import Checkbox from "@mui/material/Checkbox";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ETaxNote } from "../../../types/e-tax.type";
import { formatDate } from "../../../utils/date";
import { formatNumber } from "../../../utils";
import { DocTypeChip, headCellSx } from "../documentCells";
import NoteStatusCell from "./NoteStatusCell";
import { getNoteUnsendableReason } from "./noteSend";

const COLUMN_COUNT = 13;
const SKELETON_ROW_COUNT = 10;

interface NoteSendTableProps {
  notes: ETaxNote[];
  loading: boolean;
  page: number;
  limit: number;
  /** The notes picked so far, by uuid, across pages. */
  selected: Record<string, ETaxNote>;
  onToggleNote: (note: ETaxNote) => void;
  /** Ticks every note on the page that can be sent, or clears the page. */
  onToggleAllOnPage: (checked: boolean) => void;
}

interface NoteSendRowProps {
  note: ETaxNote;
  rowNumber: number;
  isStriped: boolean;
  isChecked: boolean;
  onToggle: (note: ETaxNote) => void;
}

function NoteSendRow({
  note,
  rowNumber,
  isStriped,
  isChecked,
  onToggle,
}: Readonly<NoteSendRowProps>) {
  const unsendableReason = getNoteUnsendableReason(note);
  const handleToggle = () => onToggle(note);

  return (
    <TableRow
      hover
      selected={isChecked}
      sx={{
        "& td, & th": { padding: "8px" },
        bgcolor: (theme) =>
          isStriped ? theme.palette.background.default : theme.palette.background.paper,
      }}
    >
      <TableCell padding="checkbox">
        {unsendableReason ? (
          <Tooltip arrow title={unsendableReason}>
            {/* A disabled input fires no pointer events; the span takes the hover. */}
            <span>
              <Checkbox checked={false} disabled />
            </span>
          </Tooltip>
        ) : (
          <Checkbox checked={isChecked} onChange={handleToggle} />
        )}
      </TableCell>
      <TableCell>{rowNumber}</TableCell>
      <TableCell>{formatDate(note.transaction_date)}</TableCell>
      <TableCell>{note.transaction_no}</TableCell>
      <TableCell>{note.original_invoice_no}</TableCell>
      <TableCell>{note.tax_id ?? "-"}</TableCell>
      <TableCell>{note.customer_name}</TableCell>
      <TableCell>
        <DocTypeChip type={note.document_type} />
      </TableCell>
      <TableCell>{note.branch_code ?? "-"}</TableCell>
      <TableCell align="right">{formatNumber(note.amount_before_vat)}</TableCell>
      <TableCell align="right">{formatNumber(note.vat_amount)}</TableCell>
      <TableCell align="right">{formatNumber(note.net_total)}</TableCell>
      <TableCell>
        <NoteStatusCell note={note} />
      </TableCell>
    </TableRow>
  );
}

/** The notes of one page with a checkbox on every note the server says can be sent. */
export default function NoteSendTable({
  notes,
  loading,
  page,
  limit,
  selected,
  onToggleNote,
  onToggleAllOnPage,
}: Readonly<NoteSendTableProps>) {
  const sendableNotes = notes.filter((note) => note.can_send);
  const allOnPageSelected =
    sendableNotes.length > 0 && sendableNotes.every((note) => selected[note.transaction_uuid]);
  const someOnPageSelected = sendableNotes.some((note) => selected[note.transaction_uuid]);

  const handleToggleAllOnPage = (event: ChangeEvent<HTMLInputElement>) =>
    onToggleAllOnPage(event.target.checked);

  return (
    <TableContainer sx={{ borderRadius: "12px" }}>
      <Table>
        <TableHead sx={{ bgcolor: (theme) => theme.palette.background.default }}>
          <TableRow>
            <TableCell padding="checkbox" sx={headCellSx}>
              <Checkbox
                checked={allOnPageSelected}
                indeterminate={someOnPageSelected && !allOnPageSelected}
                onChange={handleToggleAllOnPage}
              />
            </TableCell>
            <TableCell sx={headCellSx}>#</TableCell>
            <TableCell sx={headCellSx}>วันที่เอกสาร</TableCell>
            <TableCell sx={headCellSx}>เลขที่เอกสาร</TableCell>
            <TableCell sx={headCellSx}>ใบกำกับเดิม</TableCell>
            <TableCell sx={headCellSx}>Tax ID</TableCell>
            <TableCell sx={headCellSx}>ชื่อลูกค้า</TableCell>
            <TableCell sx={headCellSx}>ประเภท</TableCell>
            <TableCell sx={headCellSx}>สนญ.</TableCell>
            <TableCell align="right" sx={headCellSx}>
              มูลค่าสินค้า
            </TableCell>
            <TableCell align="right" sx={headCellSx}>
              ภาษีมูลค่าเพิ่ม (VAT)
            </TableCell>
            <TableCell align="right" sx={headCellSx}>
              รวมทั้งสิ้น
            </TableCell>
            <TableCell>สถานะ</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading &&
            Array.from({ length: SKELETON_ROW_COUNT }, (_, rowIndex) => (
              <TableRow key={`skeleton-${rowIndex}`}>
                {Array.from({ length: COLUMN_COUNT }, (__, cellIndex) => (
                  <TableCell key={cellIndex}>
                    <Skeleton variant="text" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          {!loading && notes.length === 0 && (
            <TableRow>
              <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 5 }}>
                <Typography color="text.secondary" fontWeight={500}>
                  ไม่พบใบลดหนี้ / ใบเพิ่มหนี้
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {!loading &&
            notes.map((note, index) => (
              <NoteSendRow
                key={note.transaction_uuid}
                note={note}
                rowNumber={(page - 1) * limit + index + 1}
                isStriped={index % 2 === 1}
                isChecked={Boolean(selected[note.transaction_uuid])}
                onToggle={onToggleNote}
              />
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
